import { z } from 'zod';
import { APPROVAL_STATUSES, APPROVAL_STEP_STATUSES, APPROVAL_TYPES, APPROVER_KINDS, } from '../enums.js';
import { isoDate, optionalText, paginated, paginationQuerySchema, queryBoolean, reasonText, uuid, } from './common.js';
/** Generic approval engine (ADR-017, FR-BANK-4/5). */
/** `GET /approvals?type=&status=&mine=`. */
export const approvalListQuerySchema = paginationQuerySchema.extend({
    type: z.enum(APPROVAL_TYPES).optional(),
    status: z.enum(APPROVAL_STATUSES).optional(),
    /** Only requests the caller submitted. */
    mine: queryBoolean.optional(),
    /** Requests about one employee (e.g. the profile banner). */
    subjectEmployeeId: uuid.optional(),
});
/** `POST /approvals/:id/approve`. Effective date of the resulting record defaults to today. */
export const approvalApproveSchema = z.object({
    effectiveDate: isoDate.optional(),
    note: optionalText(500),
});
/** `POST /approvals/:id/reject`. */
export const approvalRejectSchema = z.object({ reason: reasonText });
/** `POST /approvals/:id/cancel` (requester only, FR-BANK-5). */
export const approvalCancelSchema = z.object({ note: optionalText(500) });
/**
 * `POST /approvals/:id/steps/:stepNo/reassign` (ADR-059): a pending `USER` step goes to another
 * user who may decide steps of that type (e.g. `payroll.approve` for `PAYROLL_RUN`).
 */
export const approvalReassignSchema = z.object({ userId: uuid, note: optionalText(500) });
/** Path of the reassign route. */
export const approvalStepParamSchema = z.object({
    id: uuid,
    stepNo: z.coerce.number().int().min(1).max(20),
});
const person = z.object({ id: uuid, empCode: z.number().int(), fullName: z.string() });
/**
 * One request as listed (FR-BANK-4). `type` is text: modules register further types (ADR-017).
 * A request is about an employee (`subject`) or — subject-less types such as `PAYROLL_RUN` —
 * about what `subjectRef` names (e.g. `{ runId }`, ADR-059); `subjectLabel` is what the inbox
 * shows either way (the employee's name or e.g. "October 2026 payroll").
 * `canDecide` / `canCancel` say what this viewer may do now (decided by the API).
 */
export const approvalItemSchema = z.object({
    id: uuid,
    type: z.string(),
    typeLabel: z.string(),
    status: z.enum(APPROVAL_STATUSES),
    subject: person.nullable(),
    subjectRef: z.record(z.string(), z.string()).nullable(),
    subjectLabel: z.string(),
    /** Name of the person who asked (their employee record); null for system requests. */
    requestedBy: z.string().nullable(),
    requestedAt: z.string(),
    decidedAt: z.string().nullable(),
    currentStep: z.number().int(),
    totalSteps: z.number().int(),
    canDecide: z.boolean(),
    canCancel: z.boolean(),
});
export const approvalListResponseSchema = paginated(approvalItemSchema);
/**
 * `GET /approvals/:id`: the steps and the requested change. `changes` holds current vs
 * requested values (decrypted) and is null when this viewer may not see them (AC-6).
 */
export const approvalDetailSchema = approvalItemSchema.extend({
    note: z.string().nullable(),
    steps: z.array(z.object({
        stepNo: z.number().int(),
        approverKind: z.enum(APPROVER_KINDS),
        /** e.g. "HR", "Reporting manager", a person's name. */
        approver: z.string(),
        status: z.enum(APPROVAL_STEP_STATUSES),
        decidedBy: z.string().nullable(),
        decidedAt: z.string().nullable(),
        note: z.string().nullable(),
    })),
    changes: z
        .array(z.object({
        field: z.string(),
        label: z.string(),
        before: z.string().nullable(),
        after: z.string().nullable(),
    }))
        .nullable(),
    /** What the approval created, e.g. `{ bankTaxId }`. */
    resultRef: z.record(z.string(), z.string()).nullable(),
});
/** `GET /approvals/pending-count`: requests waiting for this viewer (sidebar badge). */
export const approvalPendingCountSchema = z.object({ count: z.number().int() });
/** `POST /employees/:id/bank/requests` → the new request (and the cancelled one, if replaced). */
export const approvalCreatedSchema = z.object({
    id: uuid,
    status: z.enum(APPROVAL_STATUSES),
    replacedRequestId: uuid.nullable(),
});
//# sourceMappingURL=approvals.js.map